import React, { useState, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';

const MobileFreeTrialModal = ({ 
  isOpen, 
  onCancel, 
  onConfirm, 
  planName,
  merchantName
}) => {
  const [endDate, setEndDate] = useState('');
  const [automaticRenewal, setAutomaticRenewal] = useState(false);

  // Calculate default end date (30 days from now)
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  // Set default end date when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultDate = getDefaultEndDate();
      setEndDate(defaultDate);
      setAutomaticRenewal(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that we have a valid end date
    if (!endDate) {
      alert('Please select an end date for your free trial.');
      return;
    }
    
    // Additional validation to ensure the date is in the future
    const selectedDate = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
    
    if (selectedDate <= today) {
      alert('End date must be in the future.');
      return;
    }
    
    // Log for debugging
    console.log('Submitting free trial with end date:', endDate);
    console.log('Automatic renewal:', automaticRenewal);
    
    onConfirm(endDate, automaticRenewal);
  };

  const handleCancel = () => {
    // Reset form when canceling
    setEndDate('');
    setAutomaticRenewal(false);
    onCancel();
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateString = maxDate.toISOString().split('T')[0];

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Start Free Trial</h3>
          <button onClick={handleCancel} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>
        
        <p style={styles.modalMessage}>
          You're starting a free trial for <strong>{planName}</strong> from <strong>{merchantName}</strong>.
          Please set when your trial ends.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <Calendar size={16} style={{ marginRight: '8px' }} />
              Trial End Date *
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                console.log('Date changed to:', e.target.value);
                setEndDate(e.target.value);
              }}
              min={today}
              max={maxDateString}
              style={styles.dateInput}
              required
            />
            <p style={styles.helpText}>
              Your trial will automatically cancel on this date
            </p>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={automaticRenewal}
                onChange={(e) => setAutomaticRenewal(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>
                Automatically convert to paid subscription after trial
              </span>
            </label>
          </div>

          <div style={styles.modalButtons}>
            <button 
              type="button"
              onClick={handleCancel} 
              style={styles.cancelButton}
            >
              Cancel
            </button>
            <button 
              type="submit"
              style={styles.confirmButton}
            >
              Start Free Trial
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(2px)",
  },
  modal: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: "12px",
    zIndex: 1001,
    width: "90%",
    maxWidth: "450px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#253985",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#666",
    padding: "4px",
    borderRadius: "4px",
  },
  modalMessage: {
    marginBottom: "20px",
    color: "#444",
    lineHeight: "1.5",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#333",
  },
  dateInput: {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    backgroundColor: "#fff",
  },
  helpText: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px",
  },
  checkboxGroup: {
    display: "flex",
    alignItems: "flex-start",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "flex-start",
    cursor: "pointer",
  },
  checkbox: {
    margin: "2px 8px 0 0",
  },
  checkboxText: {
    fontSize: "14px",
    color: "#444",
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "16px",
  },
  confirmButton: {
    backgroundColor: "#253985",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
  },
  cancelButton: {
    backgroundColor: "#f0f0f0",
    color: "#333",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
  },
};

export default MobileFreeTrialModal;